import { z } from "zod";

/** ສະກຸນທີ່ຮ້ານ / POS / ຄາຕາລັອກໃຊ້ຮ່ວມກັນ (ສອດຄ່ອງ DB) */
export const SHOP_CURRENCIES = ["LAK", "THB", "USD"] as const;
export type ShopCurrency = (typeof SHOP_CURRENCIES)[number];

export const DEFAULT_SHOP_CURRENCY: ShopCurrency = "LAK";

export const ShopCurrencySchema = z.enum(SHOP_CURRENCIES);

export function isShopCurrency(v: string): v is ShopCurrency {
  return (SHOP_CURRENCIES as readonly string[]).includes(v);
}

/** ສັນລັກສະແດງຄູ່ກັບລະຫັດສະກຸນ (FormPrice, ຟອມຕົ້ນທຶນ, ຯລຯ) */
export function shopCurrencySymbol(currency: string): string {
  const code = isShopCurrency(currency) ? currency : DEFAULT_SHOP_CURRENCY;
  const symbols: Record<ShopCurrency, string> = {
    LAK: "₭",
    THB: "฿",
    USD: "$",
  };
  return symbols[code];
}

const LAK_FRACTION = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
} as const;

const THB_USD_FRACTION = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
} as const;

/**
 * ສະແດງຍອດເງິນຕາມສະກຸນ — LAK ບໍ່ສະແດງທົດສະນິຍົມ (ສະເພາະການສະແດງ; ຄ່າໃນ DB ອາດເປັນທົດສະນິຍົມໄດ້)
 */
export function formatShopMoney(
  amount: number,
  currency: string,
  locale = "lo-LA",
): string {
  const code = isShopCurrency(currency) ? currency : DEFAULT_SHOP_CURRENCY;
  const digits = code === "LAK" ? LAK_FRACTION : THB_USD_FRACTION;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      ...digits,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

/** ຊື່ເກົ່າທີ່ໃຊ້ໃນ POS — ຄືກັນກັບ formatShopMoney */
export const formatPrice = formatShopMoney;

/**
 * ກວດວ່າຍອດລວມຊຳລະ (ໃນສະກຸນບິນ) ກັບຍອດຕ້ອງຊຳລະຕົງກັນ — ໃຊ້ຫົວໜ່ວຍ 1/100 ຂອງສະກຸນ (ຄື validate ຝັ່ງ server)
 * LAK: ອະນຸຍາດຜິດ ≤ 1 LAK (float / FX / ຄຳແນະນຳ conservative)
 * THB/USD: ຜິດ ≤ 0.01
 */
export function posPaymentTotalsMatchInvoice(
  paymentsSumInInvoice: number,
  invoiceGrandTotal: number,
  invoiceCurrency: ShopCurrency,
): boolean {
  const a = Math.round(paymentsSumInInvoice * 100);
  const b = Math.round(invoiceGrandTotal * 100);
  const tolCents = invoiceCurrency === "LAK" ? 100 : 1;
  return Math.abs(a - b) <= tolCents;
}

/**
 * ຍອດຊຳລະພໍ ຫຼື ເກີນຍອດບິນ — ອະນຸຍາດຄ້າງໃນວົງເລັກນ້ອຍ (ເທົ່າ slack ຂອງ posPaymentTotalsMatchInvoice)
 * ໃຊ້ເປີດປຸ່ມ ຢືນຢັນຊຳລະ ເມື່ອຈ່າຍເກີນ; ຄ້າງເກີນ slack ຍັງບໍ່ໃຫ້ຊຳລະ
 */
export function posPaymentMeetsOrExceedsInvoice(
  paymentsSumInInvoice: number,
  invoiceGrandTotal: number,
  invoiceCurrency: ShopCurrency,
): boolean {
  const sumC = Math.round(paymentsSumInInvoice * 100);
  const totalC = Math.round(invoiceGrandTotal * 100);
  const slack = invoiceCurrency === "LAK" ? 100 : 1;
  return sumC >= totalC - slack;
}

/**
 * ມັດຈຳຈິງ — ຍັງບໍ່ຮອດຍອດທີ່ນັບວ່າ "ຊຳລະຄົບ" (ຕາມ slack ຂອງ meets-or-exceeds)
 */
export function posPaymentIsStrictUnderpayment(
  paymentsSumInInvoice: number,
  invoiceGrandTotal: number,
  invoiceCurrency: ShopCurrency,
): boolean {
  const sumC = Math.round(paymentsSumInInvoice * 100);
  const totalC = Math.round(invoiceGrandTotal * 100);
  const slack = invoiceCurrency === "LAK" ? 100 : 1;
  if (sumC <= 0) return false;
  const minFullPaymentC = totalC - slack;
  if (minFullPaymentC <= 0) {
    return sumC < totalC;
  }
  return sumC < minFullPaymentC;
}

/** ຍອດຄ້າງໃນສະກຸນບິນ (ບໍ່ຕິດລົບ) */
export function posInvoiceBalanceDue(
  invoiceGrandTotal: number,
  paidInInvoice: number,
): number {
  const due =
    Math.round(invoiceGrandTotal * 100) - Math.round(paidInInvoice * 100);
  return Math.max(0, due / 100);
}

/** ຊື່ສຳລັບໃບເສັດ / ພິມ — ຄືກັນກັບ formatShopMoney */
export const formatMoney = formatShopMoney;
